import {WorkingAsciiLoader} from '@github-ui/agent-sessions-interface/components/WorkingAsciiLoader'
import {WithShimmerEffect} from '@github-ui/copilot-chat-visuals/WithShimmerEffect'
import {Stack} from '@primer/react'
import {useState, useRef, useEffect} from 'react'
import {clsx} from 'clsx'
import {loadingStateTips} from '../../constants/loading-tips'
import {LoadingTip} from './LoadingTip'
import styles from './LoadingMessage.module.css'

const predefinedMessages = [
  'Spinning up dev environment…',
  'Fueling the runtime engines…',
  'Clearing the airspace for MCP traffic…',
  'Cabin pressure stable. Almost ready…',
  'Reticulating goggles...',
  'Calibrating flux capacitor...',
  'Adjusting seatbelt…',
  'Waiting for clearance from GitHub tower…',
  'Final systems check, standing by…',
  'Takeoff soon™…',
]

export function BootingUpMessage() {
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const [tipIndex, setTipIndex] = useState(0)
  const [showTip, setShowTip] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    } else {
      intervalRef.current = setInterval(() => {
        setLoadingMessageIndex(prev => {
          if (prev < predefinedMessages.length - 1) {
            return prev + 1
          }
          setShowTip(true)
          return prev // stay on last message
        })
      }, 7000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!showTip || loadingStateTips.length === 0) return
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % loadingStateTips.length)
    }, 15_000)
    return () => clearInterval(interval)
  }, [showTip])

  // We should never hit the fallback case, but typescript complains if we don't handle it
  return (
    <div aria-live="polite">
      <LoadingMessage message={predefinedMessages[loadingMessageIndex] || 'Loading…'} />
      {showTip && loadingStateTips.length > 0 && loadingStateTips[tipIndex] && (
        <LoadingTip key={tipIndex} tip={loadingStateTips[tipIndex]} />
      )}
    </div>
  )
}

export function LoadingMessage({message, alignWithTools = false}: {message: string; alignWithTools?: boolean}) {
  return (
    <Stack
      direction="horizontal"
      padding="none"
      align="center"
      justify="start"
      gap="condensed"
      className={clsx(styles.loadingMessage, alignWithTools && styles.alignWithTools)}
    >
      <WorkingAsciiLoader />
      <WithShimmerEffect>{message}</WithShimmerEffect>
    </Stack>
  )
}
